import { Mail, Linkedin, UserPlus, Search, FileText } from 'lucide-react';

// Shared between ActionCard, ActionDetailModal and DailyDigestPage so the two
// card surfaces (grid row + detail modal) never drift into two different
// vocabularies for the same action.

// `tone` drives the icon tile. All kinds sit in the app's indigo family: a
// second accent competing with the brand colour read as a warning state rather
// than a price. Cost stays legible through the explicit "N contacts · N
// credits" label instead of through hue.
export const KIND_META = {
    email: { label: 'Email', icon: Mail, tone: 'bg-indigo-50 text-indigo-600' },
    linkedin_dm: { label: 'LinkedIn DM', icon: Linkedin, tone: 'bg-sky-50 text-sky-600' },
    linkedin_invite: { label: 'LinkedIn invite', icon: UserPlus, tone: 'bg-sky-50 text-sky-600' },
    fetch_contact: { label: 'Find contact', icon: Search, tone: 'bg-indigo-50 text-indigo-600' },
};

// Keyed on channelBlockedReason. `cta: null` means there is nothing for the
// user to do — e.g. a rate limit that clears on its own tomorrow.
export const BLOCKED_BANNERS = {
    gmail_not_connected: { text: 'Gmail is not connected.', cta: 'Connect Gmail', href: '/profile' },
    gmail_no_send_scope: { text: 'Gmail is connected but missing send permission.', cta: 'Connect Gmail', href: '/profile' },
    linkedin_not_connected: { text: 'LinkedIn is not connected.', cta: 'Connect LinkedIn', href: '/profile' },
    linkedin_rate_limited: { text: 'LinkedIn paused until tomorrow.', cta: null, href: null },
};

// Where the opportunity came from. The card showed the ACTION ("Find contact")
// but never the SOURCE, so a reader could not tell a hiring signal from a job
// posting — which changes what the outreach should say.
export const SOURCE_META = {
    signal: { label: 'Hiring signal', tone: 'bg-violet-50 text-violet-700 border-violet-200' },
    job: { label: 'Job posting', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    contact: { label: 'Saved contact', tone: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const STATUS_STYLES = {
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    skipped: 'bg-slate-100 text-slate-500 border-slate-200',
    sent: 'bg-blue-50 text-blue-700 border-blue-200',
    manual_sent: 'bg-blue-50 text-blue-700 border-blue-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    proposed: 'bg-white text-slate-500 border-slate-200',
};

export const STATUS_LABELS = {
    approved: 'Approved',
    skipped: 'Skipped',
    sent: 'Sent',
    manual_sent: 'Sent manually',
    failed: 'Failed',
    proposed: 'Proposed',
};

// The card's own frame reflects its state, so a scanned column reads at a
// glance: approved stands out, skipped recedes.
export const CARD_STATE_STYLES = {
    approved: 'border-emerald-200 ring-1 ring-emerald-100',
    skipped: 'border-slate-200 opacity-60',
    sent: 'border-blue-200 ring-1 ring-blue-100',
    manual_sent: 'border-blue-200 ring-1 ring-blue-100',
    failed: 'border-red-200 ring-1 ring-red-100',
    proposed: 'border-slate-200',
};

export const kindMetaFor = (kind) => KIND_META[kind] || { label: kind, icon: FileText, tone: 'bg-slate-100 text-slate-500' };
export const sourceMetaFor = (type) => SOURCE_META[type] || { label: 'Opportunity', tone: 'bg-slate-100 text-slate-600 border-slate-200' };

// The only values the server's enum on contactsRequested / defaultContactsPerFetch accepts.
export const CONTACTS_PER_FETCH_OPTIONS = [10, 20, 30, 40, 50];

/**
 * ONE CREDIT PER CONTACT — the price the whole product charges, mirrored here
 * purely so the cost updates instantly while the PATCH is in flight. The server
 * recomputes and returns the authoritative value.
 *
 * Not to be confused with what Hunter bills Career Champ (1 credit per 10
 * emails); that is cost of goods, and pricing this screen off it made the same
 * fetch ten times cheaper here than everywhere else in the app.
 */
export const creditsFor = (contacts) => Number(contacts) || 0;

/**
 * Cards link out to the real job/signal page rather than an <a href>, so the
 * SPA router handles it (no full reload). jobId wins when both happen to be
 * present since a job-sourced action's opportunity is the job itself.
 */
export const navigateToOpportunity = (action, navigate) => {
    const opportunity = action?.opportunity || {};
    if (opportunity.jobId) {
        navigate(`/jobs/${opportunity.jobId}`, { state: { jobId: opportunity.jobId } });
        return true;
    }
    /**
     * Signals open the company's own signal page directly.
     *
     * `/hiring-signals-us/:ticker` is the real detail route — it is what the
     * board's own "View Details" button navigates to (see PublicSignals.js
     * `openCompany`). Searching the board with `?q=TICKER` instead left the user
     * on a one-row list they then had to click again, and `/hiringsignal/:id` is
     * a redirect to the unfiltered board, which is worse still.
     *
     * `signalId` is the ticker for every signal the agent proposes, because the
     * only signal pool is the SEC-listed one.
     */
    if (opportunity.signalId) {
        navigate(`/hiring-signals-us/${encodeURIComponent(opportunity.signalId)}`);
        return true;
    }

    // No ticker: fall back to the board searched by name. Reachable only for
    // legacy rows built before signals were narrowed to SEC-listed companies.
    const companyName = action?.contact?.companyName;
    if (companyName) {
        navigate(`/hiring-signals-us?direction=hiring&q=${encodeURIComponent(companyName)}`);
        return true;
    }
    return false;
};

/* ─────────────────────────────────────────────────────────────────────────
   Design-system vocabulary for the rebuilt Daily Digest (Today + History).
   Ported from the Claude Design project's `screens/daily-digest.jsx`.
   ───────────────────────────────────────────────────────────────────────── */

// One tone per SOURCE (where the opportunity came from), not per action kind.
// The design colours the queue by source because that is what changes how the
// row should be read — a filing-derived signal and a saved contact are
// different kinds of evidence, whatever channel the outreach eventually uses.
export const DD_TONE = {
    signal: { bg: '#F5F3FF', fg: '#6D28D9', bar: '#8B5CF6', label: 'Signal' },
    job: { bg: '#ECFDF5', fg: '#047857', bar: '#10B981', label: 'Job' },
    contact: { bg: '#EEF1FE', fg: '#3F57E0', bar: '#3F57E0', label: 'Contact' },
};

export const toneFor = (sourceType) => DD_TONE[sourceType]
    || { bg: '#F1F3F7', fg: '#334155', bar: '#94A3B8', label: 'Other' };

const clean = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * The two lines every queue row and detail header shows.
 *
 * Derived per SOURCE rather than from one generic field, because the useful
 * headline differs: a contact row is about a person, a job row is about a
 * role, and a signal row is about a company. Using `contact.name` for all
 * three left every signal and job row titled "null".
 */
export const rowIdentityFor = (action) => {
    const c = action?.contact || {};
    const o = action?.opportunity || {};
    const source = action?.source?.type;

    if (source === 'contact') {
        return {
            title: clean(c.name) || clean(c.companyName) || 'Saved contact',
            meta: [clean(c.title), clean(c.companyName)].filter(Boolean).join(' · ')
                || 'Already in your contacts',
        };
    }

    if (source === 'job') {
        return {
            title: clean(o.jobTitle) || clean(c.companyName) || 'Open role',
            meta: [clean(c.companyName), clean(o.location)].filter(Boolean).join(' · ')
                || 'Matched to your profile',
        };
    }

    // Signal — the company is the headline; the filing language is the detail.
    return {
        title: clean(c.companyName) || clean(o.signalId) || 'Hiring signal',
        // A fetch row genuinely has nobody to write to yet, and saying so is
        // more useful than repeating the company name on both lines.
        meta: action?.kind === 'fetch_contact'
            ? 'No contact yet — agent can find one'
            : [clean(c.title), clean(c.companyName)].filter(Boolean).join(' · ') || 'Hiring signal',
    };
};

// A row is either ready to send (a draft exists) or needs contacts bought
// first. This is the single distinction the queue's status line turns on.
export const rowNeedsContacts = (action) => action?.kind === 'fetch_contact';

// Statuses the user can no longer change — the executor owns them.
export const TERMINAL_STATUSES = ['sent', 'manual_sent', 'failed'];
export const isTerminal = (action) => TERMINAL_STATUSES.includes(action?.status);

/** Deterministic tile colour for a company with no logo, seeded by name. */
export const tileColorFor = (name) => {
    const palette = ['#3F57E0', '#8B5CF6', '#10B981', '#F59E0B', '#EC4899', '#0EA5E9', '#6366F1'];
    const key = String(name || '?');
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
};

export const initialsFor = (name) => String(name || '?')
    .split(/\s+/).filter(Boolean).map((s) => s[0]).slice(0, 2).join('')
    .toUpperCase() || '?';

/**
 * 'YYYY-MM-DD' → a LOCAL Date.
 *
 * `new Date('2026-08-31')` is parsed as UTC midnight, which renders as the
 * previous day for anyone west of UTC. digestDate is already the user's local
 * calendar day, so it has to be constructed from its parts.
 */
export const parseDigestDate = (digestDate) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(digestDate || ''));
    if (!match) return null;
    const [, y, m, d] = match;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return Number.isNaN(date.getTime()) ? null : date;
};

export const toLocalDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

/**
 * How far the balance actually stretches across today's proposals.
 *
 * Walks the still-open rows IN THE ORDER THEY ARE SHOWN and counts how many the
 * balance covers, because that is the order a user approving from the top
 * actually spends in. A `balance / averageCost` estimate would promise coverage
 * the user cannot reach once an expensive row sits early in the list.
 *
 * Pure, and deliberately kept here rather than in DigestSummary.jsx: that
 * module imports the redux slice, which pulls in axios, which makes this
 * arithmetic untestable without mocking the whole API layer.
 */
export const budgetCoverage = (actions, balance) => {
    // Only rows that COST something. A free message row is not covered by the
    // balance, and counting it inflated the figure with rows no credit buys.
    const open = (actions || []).filter((a) => (a.status === 'proposed' || a.status === 'approved')
        && (Number(a.creditCost) || 0) > 0);
    let spent = 0;
    let covered = 0;
    for (const a of open) {
        const cost = Number(a.creditCost) || 0;
        if (spent + cost > (Number(balance) || 0)) break;
        spent += cost;
        covered += 1;
    }
    const totalCost = open.reduce((sum, a) => sum + (Number(a.creditCost) || 0), 0);
    return { covered, total: open.length, totalCost };
};

/**
 * The `/in/<slug>` part of a LinkedIn profile URL, lowercased.
 *
 * This is the key the invite cache is stored under — the app tracks invites by
 * publicIdentifier because a contact record carries a URL, not LinkedIn's
 * internal provider id. Returns null rather than a guess when the URL is not a
 * profile link.
 */
export const publicIdentifierFrom = (profileUrl) => {
    const parts = String(profileUrl || '').split('/in/');
    if (parts.length < 2) return null;
    const slug = parts[1].split('?')[0].replace(/\/+$/, '').trim().toLowerCase();
    return slug || null;
};

/**
 * What a status means ON THIS ROW, not in the abstract.
 *
 * `sent` is not one thing: on a fetch_contact row it means contacts were
 * bought, on an email row it means an email left the user's Gmail, on an invite
 * row it means LinkedIn received a connection request. A single "Sent" label
 * flattened three different outcomes, and "Email sent" on a contact-fetch row
 * would be plainly false.
 */
export const SEND_VERB = {
    email: 'Email',
    linkedin_invite: 'LinkedIn invite',
    linkedin_dm: 'LinkedIn message',
};

export const statusLabelFor = (action) => {
    const status = action?.status;
    const kind = action?.kind;

    if (status === 'sent' || status === 'manual_sent') {
        if (kind === 'fetch_contact') return 'Contacts found';
        if (kind === 'email') return 'Email sent';
        if (kind === 'linkedin_invite') return 'Invite sent';
        if (kind === 'linkedin_dm') return 'Message sent';
        return 'Sent';
    }
    return STATUS_LABELS[status] || status;
};

/** "Send Email now", "Send LinkedIn invite now" — the action, spelled out. */
export const sendButtonLabelFor = (action) =>
    `Send ${SEND_VERB[action?.kind] || 'message'} now`;

/**
 * "Email sent · 31 Aug" for a contact who has already been emailed.
 *
 * WHY THE BUTTON, not a badge somewhere. A contact who has been emailed keeps
 * their address on the card, so the card showed an email address directly above
 * a button reading "Send LinkedIn invite now" and never explained the gap. The
 * button is where the user looks for "what can I do here", so that is where the
 * answer belongs.
 *
 * The invite is not lost: the blue LinkedIn bar higher up the card has its own
 * invite control, which is why this may take the primary slot outright.
 *
 * Returns null when the contact has never been emailed, so the caller can fall
 * back to the normal send button.
 */
export const priorEmailLabelFor = (action) => {
    const raw = action?.emailSentAt;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    // "31 Aug" — no year, because a digest only ever looks weeks back.
    const when = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    return `Email sent · ${when}`;
};

/**
 * The one-line "what will happen if I press send" hint.
 *
 * It used to read "Sends from <your gmail> — counts against your daily limit"
 * on EVERY row, including LinkedIn invites. A user looking at a failed invite
 * saw a Gmail address and reasonably concluded Gmail had failed. The hint has
 * to name the channel it is actually talking about, and the limit that channel
 * actually consumes — email and invites are governed by different budgets.
 */
export const sendHintFor = (action, senderEmail) => {
    const kind = action?.kind;

    if (kind === 'fetch_contact') {
        const c = action?.creditCost || 0;
        return `${c} credit${c === 1 ? '' : 's'} · charged when you execute`;
    }
    if (kind === 'email') {
        return `Sends from ${senderEmail || 'your connected Gmail'} · counts against your daily email cap`;
    }
    if (kind === 'linkedin_invite') {
        return 'Sends as a LinkedIn invite · counts against your weekly invite limit';
    }
    if (kind === 'linkedin_dm') {
        return 'Sends as a LinkedIn message from your connected account';
    }
    return '';
};

/**
 * The state a queue row is IN, named exactly as the row already labels itself.
 *
 * WHY IT MIRRORS THE ROW. This exists to drive the status filter, and a filter
 * whose options do not match the words on the rows is worse than no filter: the
 * user picks "Failed", sees rows that say something else, and stops trusting
 * both. So the labels come from the same helpers the row renders with —
 * statusLabelFor for settled rows, and the row's own two proposed states.
 *
 * `key` is stable for filtering; `label` is what the user reads.
 */
export const rowStateFor = (action) => {
    const status = action?.status;

    if (status === 'failed') return { key: 'failed', label: 'Failed' };
    if (status === 'approved') return { key: 'approved', label: 'Approved' };
    if (status === 'skipped') return { key: 'skipped', label: 'Skipped' };
    if (status === 'queued') return { key: 'queued', label: 'Queued' };
    if (status === 'sending') return { key: 'sending', label: 'Sending' };

    if (status === 'sent' || status === 'manual_sent') {
        // Keyed by kind, not by status: "Email sent" and "Invite sent" are
        // different answers to "what happened here" and filtering them together
        // would defeat the point.
        return { key: `sent_${action?.kind || 'other'}`, label: statusLabelFor(action) };
    }

    /**
     * Emailed on an EARLIER day, so the row itself is only "proposed".
     *
     * Deliberately shares the `sent_email` key with a row sent from this
     * digest. To the reader they are one fact — "this person has been emailed" —
     * and splitting them would put two options both reading "Email sent" in the
     * status filter, which is worse than useless.
     *
     * Without this the queue showed "Draft ready" for someone emailed hours
     * earlier, contradicting the detail pane beside it.
     */
    if (action?.emailSentAt) return { key: 'sent_email', label: 'Email sent' };

    // Invited (from here or My Network) but never emailed. Shares the key a row
    // sent as an invite from this digest uses, for the same reason as above.
    if (action?.inviteSentAt) return { key: 'sent_linkedin_invite', label: 'Invite sent' };

    // Proposed. The row shows one of exactly two things.
    if (rowNeedsContacts(action)) return { key: 'needs_contacts', label: 'Needs contacts' };
    return { key: 'draft_ready', label: 'Draft ready' };
};

/**
 * The states actually present in a set of rows, with counts, ready to render.
 *
 * Only present states are returned. An option reading "Failed 0" invites a
 * click that empties the list for no reason, so a state nobody is in is not
 * offered at all.
 */
/**
 * What has already gone to this person, as the row should say it, or null.
 *
 * Both halves are shown because "Email sent" alone hid a LinkedIn invite that
 * also went out, and the reader decides whether to follow up on exactly that.
 * A send made from THIS digest counts as well as the backend's stamps for
 * earlier sends.
 */
export const contactedLabelFor = (action) => {
    const sentHere = action?.status === 'sent' || action?.status === 'manual_sent';
    const emailed = Boolean(action?.emailSentAt) || (sentHere && action?.kind === 'email');
    const invited = Boolean(action?.inviteSentAt) || (sentHere && action?.kind === 'linkedin_invite');
    if (emailed && invited) return 'Email sent · Invite sent';
    if (emailed) return 'Email sent';
    if (invited) return 'Invite sent';
    return null;
};

/**
 * Queue order: people never reached first, then people already contacted,
 * each group by the agent's score.
 *
 * Contacted people stay listed so the user can follow up, but they sink to the
 * bottom so the day opens on someone new. Jobs and signals are never
 * "contacted", so they keep pure score order.
 */
export const byFreshThenBest = (a, b) => {
    const reached = (x) => (contactedLabelFor(x) ? 1 : 0);
    return (reached(a) - reached(b)) || ((Number(b.score) || 0) - (Number(a.score) || 0));
};

export const rowStateOptions = (actions = []) => {
    const counts = new Map();
    for (const action of actions) {
        const { key, label } = rowStateFor(action);
        const existing = counts.get(key);
        if (existing) existing.n += 1;
        else counts.set(key, { key, label, n: 1 });
    }
    // Most-populated first: the state the user most likely wants to isolate is
    // rarely the alphabetically-first one.
    return [...counts.values()].sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
};


/**
 * The URL that starts Google's consent flow directly.
 *
 * Extracted so the digest's connection strip and a blocked send button cannot
 * drift apart, and so neither reintroduces the `/profile?connectGoogle=1`
 * bounce — that worked, but the user watched a whole page load and repaint
 * before the consent screen appeared, on a page they never asked for.
 *
 * `connectEmail` pins the grant to the logged-in account, so a user with a
 * second Google account in the chooser cannot silently attach the wrong one.
 * `returnTo` pins where the callback hands the user back; without it the
 * backend falls back to CLIENT_URL, which on a developer machine is the
 * DEPLOYED frontend — so a local reconnect finished on staging and the badge
 * here never appeared to change.
 *
 * Returns null with no email: there is no account to pin the grant to, and a
 * consent screen that attaches the wrong account is worse than no button.
 */
export const googleConnectUrl = ({ baseURL, userEmail, origin }) => {
    if (!userEmail) return null;
    const base = baseURL || origin || '';
    return `${base}/api/auth/google?connectEmail=${encodeURIComponent(userEmail)}`
        + `&returnTo=${encodeURIComponent(origin || '')}`;
};


/**
 * Merge a single-row response from the API into the row already on screen.
 *
 * The per-row endpoints (send, draft, approve, mark sent) return the row as
 * stored. Read-time facts are not stored: `emailSentAt`, `inviteSentAt`, and
 * the corrected "why now" are added only when a whole digest is read. Replacing
 * the row wholesale therefore wiped "Email sent · Invite sent" the moment the
 * user did anything to it, until the page reloaded.
 *
 * So those three are carried over from the row on screen whenever the response
 * does not supply them. Everything else comes from the server.
 */
export const mergeActionUpdate = (previous, next) => {
    if (!next) return previous;
    if (!previous) return next;
    return {
        ...next,
        emailSentAt: next.emailSentAt || previous.emailSentAt || null,
        inviteSentAt: next.inviteSentAt || previous.inviteSentAt || null,
        source: { ...(next.source || {}), whyNow: previous.source?.whyNow ?? next.source?.whyNow ?? null },
    };
};
